/**
 * Onboarding Presentation
 *
 * Step 3: Spotify Wrapped-style presentation of findings.
 * Shows 8 slides with animations, navigation dots, and "Get Started" CTA.
 */

import { useEffect, useCallback, useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react'
import { cn } from '@trafiflow/shared-ui'
import { useOnboarding } from '../../contexts/OnboardingContext'
import {
  BrandRevealSlide,
  MetaIdentitySlide,
  AdsCollectionSlide,
  CompetitorSlide,
  PatternsSlide,
  StrategySlide,
  CreativeBlueprintSlide,
  ProductBibleSlide,
  ClipBankSlide,
  FinalAdSlide,
} from './slides'

const TOTAL_SLIDES = 10

// Scattered image positions for background collage effect
const SCATTERED_POSITIONS = [
  { top: '5%', left: '3%', rotate: -15, scale: 0.8 },
  { top: '15%', right: '5%', rotate: 12, scale: 0.7 },
  { bottom: '20%', left: '2%', rotate: 8, scale: 0.75 },
  { top: '40%', left: '8%', rotate: -8, scale: 0.65 },
  { bottom: '10%', right: '3%', rotate: -12, scale: 0.8 },
  { top: '8%', left: '15%', rotate: 5, scale: 0.6 },
  { bottom: '35%', right: '8%', rotate: 15, scale: 0.7 },
  { top: '60%', right: '12%', rotate: -5, scale: 0.55 },
]

export default function OnboardingPresentation() {
  const { state, completeOnboarding, setSlideIndex, nextSlide, prevSlide, retry } = useOnboarding()
  const { currentSlideIndex, artifacts } = state
  const [validatedImages, setValidatedImages] = useState<string[]>([])

  // Extract candidate images from artifacts for background
  const candidateImages = useMemo(() => {
    const images: string[] = []

    // Prioritize ad thumbnails from adsCollection (these are actual ad images)
    const adsCollection = artifacts.adsCollection as {
      thumbnails?: string[]
      ads?: Array<{ thumbnail_url?: string; media_urls?: string[]; media_type?: string }>
    } | null

    // Get image ads first (more likely to have good thumbnails)
    if (adsCollection?.ads) {
      const imageAds = adsCollection.ads
        .filter(ad => ad.media_type !== 'video' && (ad.thumbnail_url || ad.media_urls?.[0]))
        .map(ad => ad.thumbnail_url || ad.media_urls![0])
        .filter(url => url?.startsWith('http'))
        .slice(0, 6)
      images.push(...imageAds)

      // Then add video thumbnails
      const videoAds = adsCollection.ads
        .filter(ad => ad.media_type === 'video' && (ad.thumbnail_url || ad.media_urls?.[0]))
        .map(ad => ad.thumbnail_url || ad.media_urls![0])
        .filter(url => url?.startsWith('http'))
        .slice(0, 8 - images.length)
      images.push(...videoAds)
    }

    if (adsCollection?.thumbnails && images.length < 8) {
      const thumbs = adsCollection.thumbnails
        .filter(url => url?.startsWith('http'))
        .slice(0, 8 - images.length)
      images.push(...thumbs)
    }

    // Fallback to brand images
    if (images.length < 8) {
      const brand = artifacts.brand as { brand_logo_url?: string; logo_url?: string; product_images?: string[]; brand_profile?: { logo_url?: string } } | null
      if (brand?.product_images) {
        const productImgs = brand.product_images
          .filter(url => url?.startsWith('http'))
          .slice(0, 8 - images.length)
        images.push(...productImgs)
      }
      // Only add logo if we have very few images
      if (images.length < 3) {
        const logoUrl = brand?.brand_logo_url || brand?.logo_url || brand?.brand_profile?.logo_url
        if (logoUrl?.startsWith('http')) {
          images.push(logoUrl)
        }
      }
    }

    return images.slice(0, 8)
  }, [artifacts])

  // Validate images by preloading them
  useEffect(() => {
    if (candidateImages.length === 0) {
      setValidatedImages([])
      return
    }

    const validated: string[] = []
    let completed = 0

    candidateImages.forEach(url => {
      const img = new window.Image()
      img.onload = () => {
        validated.push(url)
        completed++
        if (completed === candidateImages.length) {
          setValidatedImages(validated.slice(0, 8))
        }
      }
      img.onerror = () => {
        completed++
        if (completed === candidateImages.length) {
          setValidatedImages(validated.slice(0, 8))
        }
      }
      img.src = url
    })
  }, [candidateImages])

  // Use validated images or show placeholders
  const brandImages = validatedImages.length > 0 ? validatedImages : ['placeholder']

  // Keyboard navigation
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'ArrowRight' || e.key === ' ') {
      e.preventDefault()
      nextSlide()
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault()
      prevSlide()
    }
  }, [nextSlide, prevSlide])

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  // Handle browser back button
  useEffect(() => {
    const handlePopState = () => {
      if (currentSlideIndex > 0) {
        prevSlide()
      }
    }

    window.addEventListener('popstate', handlePopState)

    // Push state for each slide
    if (currentSlideIndex > 0) {
      window.history.pushState({ slide: currentSlideIndex }, '')
    }

    return () => window.removeEventListener('popstate', handlePopState)
  }, [currentSlideIndex, prevSlide])

  const renderSlide = () => {
    switch (currentSlideIndex) {
      case 0:
        return <BrandRevealSlide key="brand" artifacts={artifacts} />
      case 1:
        return <MetaIdentitySlide key="meta" artifacts={artifacts} />
      case 2:
        return <AdsCollectionSlide key="ads" artifacts={artifacts} />
      case 3:
        return <CompetitorSlide key="competitor" artifacts={artifacts} />
      case 4:
        return <PatternsSlide key="patterns" artifacts={artifacts} />
      case 5:
        return <StrategySlide key="strategy" artifacts={artifacts} />
      case 6:
        return <CreativeBlueprintSlide key="blueprint" artifacts={artifacts} />
      case 7:
        return <ProductBibleSlide key="productbible" artifacts={artifacts} />
      case 8:
        return <ClipBankSlide key="clipbank" artifacts={artifacts} />
      case 9:
        return <FinalAdSlide key="final" artifacts={artifacts} onComplete={completeOnboarding} />
      default:
        return null
    }
  }

  return (
    <div className="min-h-[calc(100vh-64px)] flex flex-col">
      {/* Main content area */}
      <div className="flex-1 flex items-center justify-center p-6 relative overflow-hidden">
        {/* Gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-tf-bg-muted via-tf-bg-card to-tf-bg-muted" />

        {/* Scattered brand images or decorative shapes in background */}
        {brandImages[0] === 'placeholder' ? (
          // Show decorative gradient shapes when no images
          <>
            {SCATTERED_POSITIONS.map((pos, index) => (
              <motion.div
                key={`bg-shape-${index}`}
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 0.08, scale: pos.scale }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="absolute pointer-events-none"
                style={{
                  top: pos.top,
                  left: pos.left,
                  right: pos.right,
                  bottom: pos.bottom,
                  transform: `rotate(${pos.rotate}deg)`,
                  zIndex: 1,
                }}
              >
                <div
                  className="w-32 h-32 md:w-40 md:h-40 rounded-2xl shadow-lg"
                  style={{
                    background: `linear-gradient(135deg,
                      ${['#3B82F6', '#8B5CF6', '#EC4899', '#10B981', '#F59E0B', '#6366F1', '#14B8A6', '#F43F5E'][index % 8]} 0%,
                      ${['#8B5CF6', '#EC4899', '#F59E0B', '#3B82F6', '#10B981', '#14B8A6', '#6366F1', '#F43F5E'][index % 8]} 100%)`,
                  }}
                />
              </motion.div>
            ))}
          </>
        ) : (
          // Show actual brand images
          brandImages.map((imageUrl, index) => {
            const pos = SCATTERED_POSITIONS[index % SCATTERED_POSITIONS.length]
            return (
              <motion.div
                key={`bg-img-${index}`}
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 0.15, scale: pos.scale }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="absolute pointer-events-none"
                style={{
                  top: pos.top,
                  left: pos.left,
                  right: pos.right,
                  bottom: pos.bottom,
                  transform: `rotate(${pos.rotate}deg)`,
                  zIndex: 1,
                }}
              >
                <img
                  src={imageUrl}
                  alt=""
                  className="w-32 h-32 md:w-40 md:h-40 object-cover rounded-2xl shadow-lg"
                  style={{
                    filter: 'grayscale(30%)',
                  }}
                />
              </motion.div>
            )
          })
        )}

        {/* Animated background orbs */}
        <div className="absolute top-1/4 -left-32 w-64 h-64 rounded-full bg-blue-500/5 blur-3xl" />
        <div className="absolute bottom-1/4 -right-32 w-64 h-64 rounded-full bg-purple-500/5 blur-3xl" />

        {/* Slide content */}
        <div className="relative z-10 w-full max-w-2xl">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlideIndex}
              initial={{ x: 100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -100, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="px-4"
            >
              {renderSlide()}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navigation arrows */}
        {currentSlideIndex > 0 && (
          <button
            onClick={prevSlide}
            className={cn(
              'absolute left-6 top-1/2 -translate-y-1/2 z-20',
              'w-12 h-12 rounded-full flex items-center justify-center',
              'bg-tf-bg-card/80 backdrop-blur border border-tf-border',
              'text-tf-text-muted hover:text-tf-text hover:bg-tf-bg-hover',
              'transition-all cursor-pointer'
            )}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}

        {currentSlideIndex < TOTAL_SLIDES - 1 && (
          <button
            onClick={nextSlide}
            className={cn(
              'absolute right-6 top-1/2 -translate-y-1/2 z-20',
              'w-12 h-12 rounded-full flex items-center justify-center',
              'bg-tf-bg-card/80 backdrop-blur border border-tf-border',
              'text-tf-text-muted hover:text-tf-text hover:bg-tf-bg-hover',
              'transition-all cursor-pointer'
            )}
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        )}
      </div>

      {/* Navigation dots */}
      <div className="flex items-center justify-center gap-2 py-6">
        {Array.from({ length: TOTAL_SLIDES }).map((_, index) => (
          <button
            key={index}
            onClick={() => setSlideIndex(index)}
            className={cn(
              'transition-all duration-300 cursor-pointer',
              index === currentSlideIndex
                ? 'w-8 h-2 rounded-full bg-primary'
                : 'w-2 h-2 rounded-full bg-tf-border hover:bg-tf-text-muted'
            )}
          />
        ))}
      </div>

      {/* Keyboard hint and restart button */}
      <div className="text-center pb-4 flex flex-col items-center gap-3">
        <p className="text-xs text-tf-text-muted">
          Use arrow keys or click to navigate
        </p>
        <button
          onClick={retry}
          className={cn(
            'inline-flex items-center gap-2 px-4 py-2 rounded-lg',
            'text-sm text-tf-text-muted hover:text-tf-text',
            'bg-tf-bg-card/50 hover:bg-tf-bg-hover border border-tf-border',
            'transition-all cursor-pointer'
          )}
        >
          <RotateCcw className="w-4 h-4" />
          Start Over
        </button>
      </div>
    </div>
  )
}
